import { message, Upload, UploadFile, UploadProps } from "antd";
import type { RcFile } from "antd/es/upload";
import { PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from "react";

import styles from "./index.module.css";
import { InputFileTile } from "./InputFileTile";
import { ChatsContext, FileDataType } from "context/chatsContext";
import { AdminContext } from "context/adminContext";
import { AppConfigContext } from "context/appConfigContext";
import { useDebounce } from "ahooks";
import { HEIC_ACCEPT } from "helpers/constants";
import { postTmpFileChat } from "helpers/backend_helper";

type Props = {
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  fileList: UploadFile[];
  isArbitr: boolean;
  chatId: string;
  uploadRef: React.MutableRefObject<any>;
};

const UPLOAD_ERROR_KEY = "chat-files-upload-error";

export const UploadFilesComponent = (props: PropsWithChildren<Props>) => {
  const { isArbitr, chatId } = props;
  const { localCountry } = useContext(AdminContext);
  const { fileList, setFileList, setBunchFiles } = useContext(ChatsContext);
  const [isAlotFiles, setIsAlotFiles] = useState(false);
  const [isBigFiles, setIsBigFiles] = useState(false);
  const isBigFilesMB = useDebounce(isBigFiles, { wait: 100 });
  const isMoreThanAmount = useDebounce(isAlotFiles, { wait: 100 });
  const { data: appSettings } = useContext(AppConfigContext);
  // Отмена загрузки при удалении карточки
  const uploadControllers = useRef(new Map<string, AbortController>());
  // Один общий алерт на пачку файлов, пока поле не очистится
  const isUploadErrorShown = useRef(false);

  useEffect(() => {
    if (!fileList.length) {
      isUploadErrorShown.current = false;
      return;
    }
    const isDone = fileList.every(
      (file) => file.status === "done" || file.status === "error" || file.status === "removed",
    );
    setBunchFiles((prev) => ({ ...prev, status: isDone ? "DONE" : "LOADING" }));
  }, [fileList, setBunchFiles]);

  // Загружаем сами, а не через action antd: так есть прогресс, отмена и повтор
  const uploadFile = useCallback(
    (item: UploadFile) => {
      const updateItem = (patch: Partial<UploadFile>) =>
        setFileList((prev) => prev.map((file) => (file.uid === item.uid ? { ...file, ...patch } : file)));
      setFileList((prev) =>
        prev.some((file) => file.uid === item.uid)
          ? prev.map((file) => (file.uid === item.uid ? { ...file, status: "uploading", percent: 0 } : file))
          : [...prev, { ...item, status: "uploading", percent: 0 }],
      );
      setBunchFiles((prev) => ({ ...prev, status: "LOADING" }));

      const controller = new AbortController();
      uploadControllers.current.set(item.uid, controller);
      const formData = new FormData();
      formData.append("file", item.originFileObj);
      postTmpFileChat(chatId, formData, isArbitr, {
        signal: controller.signal,
        headers: { "country-iso-code": localCountry.iso, "accept-language": "EN" },
        onUploadProgress: (event) => {
          if (!event.total) return;
          updateItem({ percent: Math.round((event.loaded / event.total) * 100) });
        },
      })
        .then((response: FileDataType) => {
          updateItem({ status: "done", percent: 100, response });
          setBunchFiles((prev) => ({ ...prev, files: [...prev.files, response] }));
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error("Failed to upload chat file", error);
          updateItem({ status: "error" });
          if (!isUploadErrorShown.current) {
            isUploadErrorShown.current = true;
            message.error({ key: UPLOAD_ERROR_KEY, content: "Error while loading some files. Try again." });
          }
        })
        .finally(() => {
          if (uploadControllers.current.get(item.uid) === controller) {
            uploadControllers.current.delete(item.uid);
          }
        });
    },
    [chatId, isArbitr, localCountry.iso, setBunchFiles, setFileList],
  );

  const removeFile = (item: UploadFile) => {
    uploadControllers.current.get(item.uid)?.abort();
    uploadControllers.current.delete(item.uid);
    const restFiles = fileList.filter((file) => file.uid !== item.uid);
    setFileList(restFiles);
    setBunchFiles((prev) => ({
      files: item.response ? prev.files.filter((file) => file.filePath !== item.response.filePath) : prev.files,
      status: restFiles.length ? prev.status : "READY",
    }));
  };

  const uploadPropsFile: UploadProps = {
    accept: `${
      appSettings?.value?.file_types_chat ||
      "image/png,image/jpeg,image/jpg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
    },${HEIC_ACCEPT}`,
    multiple: true,
    showUploadList: false,
    beforeUpload: (file: RcFile, currentFileList: RcFile[]) => {
      if (
        fileList.length + 1 > appSettings?.value?.max_photo_upload_count ||
        currentFileList.length > appSettings?.value?.max_photo_upload_count
      ) {
        setIsAlotFiles(true);
        return Upload.LIST_IGNORE;
      }

      if (file.size > 1024 * 1024 * appSettings?.value?.max_photo_upload_size) {
        setIsBigFiles(true);
        return Upload.LIST_IGNORE;
      }
      uploadFile({ uid: file.uid, name: file.name, size: file.size, type: file.type, originFileObj: file });
      return Upload.LIST_IGNORE;
    },
  };

  useEffect(() => {
    if (isMoreThanAmount) {
      message.error(`You can't load more than ${appSettings?.value?.max_photo_upload_count} files`);
      setIsAlotFiles(false);
    }
  }, [isMoreThanAmount, appSettings?.value?.max_photo_upload_count]);

  useEffect(() => {
    if (isBigFilesMB) {
      message.error(`You can't load a file that more than ${appSettings?.value?.max_photo_upload_size}MB`);
      setIsBigFiles(false);
    }
  }, [isBigFilesMB, appSettings?.value?.max_photo_upload_size]);

  return (
    <div className={fileList.length ? styles.uploadFiles + " " + styles.fileIn : styles.uploadFiles}>
      {fileList.length ? (
        <div className={styles.filesRow}>
          {fileList.map((file) => (
            <InputFileTile
              key={file.uid}
              file={file}
              onRetry={() => uploadFile(file)}
              onDelete={() => removeFile(file)}
            />
          ))}
        </div>
      ) : null}
      <Upload {...uploadPropsFile} ref={props.uploadRef} />
    </div>
  );
};
